// Единый логгер фронтенда: действия UI, консоль, сеть, необработанные ошибки.
// Копит события в буфер и батчами шлёт на сервер (POST /api/logs/).
// Самоинициализируется при импорте (идемпотентно).
//
// API_URL импортируется из ./api и используется ТОЛЬКО внутри функций (lazy),
// поэтому циклический импорт api <-> logger безопасен.

import { API_URL } from "./api";

const LS_UID_KEY = "client_uid";
const FLUSH_INTERVAL_MS = 7000; // периодическая отправка
const FLUSH_THRESHOLD = 25;     // отправка по достижении размера буфера
const MAX_BATCH = 200;          // максимум записей в одной отправке

// Оригинальный fetch сохраняем ДО патча — им шлём сами логи (без рекурсии)
const _origFetch =
  typeof window !== "undefined" && window.fetch ? window.fetch.bind(window) : null;

let buffer = [];
let flushTimer = null;
let initialized = false;

// ── client_uid (устойчивый id браузера) ─────────────────────────────────────────
export function getClientUid() {
  try {
    let uid = localStorage.getItem(LS_UID_KEY);
    if (!uid) {
      uid =
        (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
        `uid-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem(LS_UID_KEY, uid);
    }
    return uid;
  } catch (e) {
    return "uid-nostorage";
  }
}

// ── Запись события в буфер ───────────────────────────────────────────────────────
export function logEvent(entry = {}) {
  try {
    buffer.push({
      level: entry.level || "info",
      source: entry.source || "ui",
      event: entry.event || "",
      message: entry.message || "",
      url:
        entry.url ||
        (typeof window !== "undefined"
          ? window.location.pathname + window.location.search
          : ""),
      book_id: entry.book_id || null,
      order_id: entry.order_id || null,
      payload: entry.payload || {},
      client_ts: new Date().toISOString(),
    });
    if (buffer.length > MAX_BUFFER) buffer.splice(0, buffer.length - MAX_BUFFER);
    if (entry.level === "error" || buffer.length >= FLUSH_THRESHOLD) {
      flushLogs();
    }
  } catch (e) {
    /* логгер никогда не должен ломать приложение */
  }
}

// Короткий помощник для действий пользователя
export function logUi(event, message, payload = {}) {
  logEvent({ level: "info", source: "ui", event, message, payload });
}

// ── Отправка батча ──────────────────────────────────────────────────────────────
let flushInFlight = null;
const MAX_BUFFER = 5000;

export function flushLogs({ useBeacon = false } = {}) {
  if (!buffer.length) return flushInFlight;
  if (flushInFlight && !useBeacon) return flushInFlight;
  const batch = buffer.splice(0, MAX_BATCH);
  const body = JSON.stringify({ client_uid: getClientUid(), entries: batch });
  const url = `${API_URL}/logs/`;
  const restore = () => { buffer = batch.concat(buffer).slice(-MAX_BUFFER); };
  try {
    if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      if (navigator.sendBeacon(url, new Blob([body], { type: "application/json" }))) return;
    }
    const f = _origFetch || window.fetch;
    const pending = f(url, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" }, body, keepalive: true,
    }).then(response => {
      if (!response.ok) { restore(); return false; }
      return true;
    }).catch(() => { restore(); return false; });
    // pagehide может отправить отдельный маленький пакет параллельно.
    if (flushInFlight) return pending;
    flushInFlight = pending.then(ok => {
      flushInFlight = null;
      if (ok && buffer.length >= FLUSH_THRESHOLD) flushLogs();
      return ok;
    });
    return flushInFlight;
  } catch (_) {
    restore();
    return null;
  }
}

// ── Перехват консоли ──────────────────────────────────────────────────────────────
function patchConsole() {
  const map = { log: "info", info: "info", warn: "warn", error: "error" };
  Object.keys(map).forEach((method) => {
    const orig = console[method] ? console[method].bind(console) : null;
    if (!orig) return;
    console[method] = (...args) => {
      try {
        logEvent({
          level: map[method],
          source: "console",
          event: `console.${method}`,
          message: args
            .map((a) => {
              try {
                return typeof a === "string" ? a : JSON.stringify(a);
              } catch (e) {
                return String(a);
              }
            })
            .join(" ")
            .slice(0, 2000),
        });
      } catch (e) {}
      orig(...args);
    };
  });
}

// ── Перехват необработанных ошибок ────────────────────────────────────────────────
function patchErrors() {
  window.addEventListener("error", (e) => {
    logEvent({
      level: "error",
      source: "error",
      event: "window.onerror",
      message: (e && e.message ? e.message : "Unhandled error").slice(0, 2000),
      payload: {
        filename: e && e.filename,
        lineno: e && e.lineno,
        colno: e && e.colno,
        stack: e && e.error && e.error.stack ? e.error.stack.slice(0, 4000) : undefined,
      },
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e && e.reason;
    logEvent({
      level: "error",
      source: "error",
      event: "unhandledrejection",
      message: (
        (reason && reason.message) ||
        String(reason) ||
        "Unhandled rejection"
      ).slice(0, 2000),
      payload: { stack: reason && reason.stack ? reason.stack.slice(0, 4000) : undefined },
    });
  });
}

// ── Перехват fetch (ловит запросы на голом fetch, напр. validateCart) ──────────────
function patchFetch() {
  if (!window.fetch || !_origFetch) return;
  window.fetch = async (input, init = {}) => {
    const url = typeof input === "string" ? input : (input && input.url) || "";
    const method = (
      (init && init.method) ||
      (typeof input !== "string" && input && input.method) ||
      "GET"
    ).toUpperCase();

    // НЕ логируем сам логгер и код техподдержки — иначе рекурсия
    if (url.includes("/logs/") || url.includes("/support/code/")) {
      return _origFetch(input, init);
    }

    const started = performance.now();
    logEvent({ source: "network", event: "http_request", message: `${method} ${url}`, url });
    try {
      const res = await _origFetch(input, init);
      const ms = Math.round(performance.now() - started);
      logEvent({
        level: res.ok ? "info" : "error",
        source: "network",
        event: res.ok ? "http_response" : "http_error",
        message: `${method} ${url} → ${res.status} (${ms}ms)`,
        url,
        payload: { status: res.status, ms },
      });
      return res;
    } catch (err) {
      const ms = Math.round(performance.now() - started);
      logEvent({
        level: "error",
        source: "network",
        event: "http_error",
        message: `${method} ${url} → network error (${ms}ms): ${err && err.message ? err.message : err}`,
        url,
        payload: { ms, error: String(err && err.message ? err.message : err) },
      });
      throw err;
    }
  };
}

// ── Инициализация ──────────────────────────────────────────────────────────────────
export function initLogger() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  getClientUid(); // гарантируем наличие uid

  patchConsole();
  patchErrors();
  patchFetch();

  flushTimer = setInterval(() => flushLogs(), FLUSH_INTERVAL_MS);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushLogs({ useBeacon: true });
  });
  window.addEventListener("pagehide", () => flushLogs({ useBeacon: true }));

  logEvent({ source: "lifecycle", event: "app_init", message: "Логгер инициализирован" });
}

// Самоинициализация при импорте
initLogger();